import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Circle, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import '../../css/LocationPicker.css';

const STORE_LOCATION = { lat: 41.6510349, lng: 41.6334102 };
const MAX_DELIVERY_RADIUS_KM = 3;
const DEFAULT_ZOOM = 15;

const restaurantIcon = L.divIcon({
    className: 'lp-restaurant-marker',
    html: '<div class="lp-restaurant-marker-inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3z"/><path d="M21 15v7"/></svg></div>',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
});

function toRad(value) {
    return value * Math.PI / 180;
}

function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const CenterPin = () => {
    return (
        <div className="lp-center-pin">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#ef4444" stroke="#b91c1c" strokeWidth="1">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
            </svg>
        </div>
    );
};

const MapEventHandler = ({ onCenterChange }) => {
    const map = useMapEvents({
        moveend: () => {
            const center = map.getCenter();
            onCenterChange({ lat: center.lat, lng: center.lng });
        },
    });
    return null;
};

const FlyToLocation = ({ position }) => {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.flyTo(position, DEFAULT_ZOOM, { duration: 1 });
        }
    }, [position, map]);
    return null;
};

const LocationPicker = ({ onConfirm, onBack }) => {
    const { t } = useTranslation();
    const [mapCenter, setMapCenter] = useState(STORE_LOCATION);
    const [address, setAddress] = useState('');
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [flyTarget, setFlyTarget] = useState(null);
    const [showTooltip, setShowTooltip] = useState(true);
    const [distanceKm, setDistanceKm] = useState(0);
    const [isOutOfRange, setIsOutOfRange] = useState(false);
    const geocodeTimeoutRef = useRef(null);

    const reverseGeocode = useCallback(async (lat, lng) => {
        setIsGeocoding(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
            );
            if (!res.ok) throw new Error('Geocoding failed');
            const data = await res.json();
            setAddress(data.display_name || '');
        } catch {
            setAddress('');
        } finally {
            setIsGeocoding(false);
        }
    }, []);

    const handleCenterChange = useCallback((center) => {
        setMapCenter(center);
        setShowTooltip(false);

        const dist = calculateDistance(STORE_LOCATION.lat, STORE_LOCATION.lng, center.lat, center.lng);
        setDistanceKm(dist);
        setIsOutOfRange(dist > MAX_DELIVERY_RADIUS_KM);

        if (geocodeTimeoutRef.current) clearTimeout(geocodeTimeoutRef.current);
        geocodeTimeoutRef.current = setTimeout(() => {
            reverseGeocode(center.lat, center.lng);
        }, 800);
    }, [reverseGeocode]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    setMapCenter(loc);
                    setFlyTarget([loc.lat, loc.lng]);
                    const dist = calculateDistance(STORE_LOCATION.lat, STORE_LOCATION.lng, loc.lat, loc.lng);
                    setDistanceKm(dist);
                    setIsOutOfRange(dist > MAX_DELIVERY_RADIUS_KM);
                    reverseGeocode(loc.lat, loc.lng);
                },
                () => {
                    reverseGeocode(STORE_LOCATION.lat, STORE_LOCATION.lng);
                },
                { enableHighAccuracy: true, timeout: 8000 }
            );
        } else {
            reverseGeocode(STORE_LOCATION.lat, STORE_LOCATION.lng);
        }
    }, [reverseGeocode]);

    const handleGpsClick = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setFlyTarget([loc.lat, loc.lng]);
            },
            () => {},
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
            );
            const data = await res.json();
            if (data.length > 0) {
                const { lat, lon } = data[0];
                setFlyTarget([parseFloat(lat), parseFloat(lon)]);
            }
        } catch {
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleConfirm = () => {
        if (isOutOfRange) return;
        onConfirm({
            lat: mapCenter.lat,
            lng: mapCenter.lng,
            address,
        });
    };

    const estimatedTime = distanceKm < 1 ? '15-20' : distanceKm < 2 ? '20-30' : '30-45';

    return (
        <div className="lp-container">
            <div className="lp-header">
                <button className="lp-back-btn" onClick={onBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>
                <h2 className="lp-title">{t('locationPicker.selectLocation')}</h2>
                <div style={{ width: 40 }} />
            </div>

            <div className="lp-search-bar">
                <div className="lp-search-input-wrapper">
                    <svg className="lp-search-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" strokeWidth="2" stroke="#999" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="10" cy="10" r="7" />
                        <path d="M21 21l-6-6" />
                    </svg>
                    <input
                        className="lp-search-input"
                        type="text"
                        placeholder={t('locationPicker.searchAddress')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                </div>
                <button className="lp-gps-btn" onClick={handleGpsClick}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                        <path d="M12 2v2" />
                        <path d="M12 20v2" />
                        <path d="M2 12h2" />
                        <path d="M20 12h2" />
                    </svg>
                </button>
            </div>

            <div className="lp-map-wrapper">
                <MapContainer
                    center={[STORE_LOCATION.lat, STORE_LOCATION.lng]}
                    zoom={DEFAULT_ZOOM}
                    className="lp-map"
                    zoomControl={false}
                    attributionControl={false}
                >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Circle
                        center={[STORE_LOCATION.lat, STORE_LOCATION.lng]}
                        radius={MAX_DELIVERY_RADIUS_KM * 1000}
                        pathOptions={{
                            color: '#ef4444',
                            fillColor: '#ef4444',
                            fillOpacity: 0.08,
                            weight: 2,
                            dashArray: '6 4',
                        }}
                    />
                    <Marker position={[STORE_LOCATION.lat, STORE_LOCATION.lng]} icon={restaurantIcon} />
                    <MapEventHandler onCenterChange={handleCenterChange} />
                    {flyTarget && <FlyToLocation position={flyTarget} />}
                </MapContainer>
                <CenterPin />
                {showTooltip && (
                    <div className="lp-tooltip">{t('locationPicker.moveMap')}</div>
                )}
            </div>

            <div className="lp-address-panel">
                <div className="lp-address-label">{t('locationPicker.deliveryAddress')}</div>
                <div className="lp-address-text">
                    {isGeocoding ? t('locationPicker.findingAddress') : (address || t('locationPicker.movePin'))}
                </div>
                {!isOutOfRange && address && (
                    <div className="lp-delivery-time">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke="#999" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 6v6l4 2" />
                        </svg>
                        {t('locationPicker.est')} {estimatedTime} {t('locationPicker.min')}
                    </div>
                )}
                {isOutOfRange && (
                    <div className="lp-out-of-range">
                        {t('locationPicker.outsideDeliveryArea')} ({distanceKm.toFixed(1)}km)
                    </div>
                )}
                <button
                    className="lp-confirm-btn"
                    onClick={handleConfirm}
                    disabled={isOutOfRange || !address || isGeocoding}
                >
                    {t('locationPicker.confirmLocation')} <span>&rarr;</span>
                </button>
            </div>
        </div>
    );
};

export default LocationPicker;
